import { Box, Dialog, DialogActions, DialogContent, IconButton, Typography } from "@mui/material"
import { CloseOutlined, WarningOutlined } from "@mui-symbols-material/w300"
import type { FC, ReactNode } from "react"
import { useState } from "react"

import { Button } from "@/components/common/Button"
import { AerosTextField } from "@/components/UiKit/AerosTextField/AerosTextField"

interface InvoiceCommentConfirmDialogProps {
  title: string
  content?: ReactNode
  commentLabel: string
  commentPlaceholder?: string
  confirmLabel: string
  closeLabel?: string
  warning?: boolean
  isProcessing?: boolean
  isSubmitDisabled?: boolean
  banner?: ReactNode
  onClose: () => void
  onConfirm: (comment: string) => void
  testIdPrefix: string
}

export const InvoiceCommentConfirmDialog: FC<InvoiceCommentConfirmDialogProps> = ({
  title,
  content,
  commentLabel,
  commentPlaceholder,
  confirmLabel,
  closeLabel = "Go Back",
  warning,
  isProcessing,
  isSubmitDisabled,
  banner,
  onClose,
  onConfirm,
  testIdPrefix,
}) => {
  const [comment, setComment] = useState("")
  const trimmedComment = comment.trim()
  const isConfirmEnabled = Boolean(trimmedComment) && !isProcessing && !isSubmitDisabled

  const handleConfirm = () => {
    if (!isConfirmEnabled) return
    onConfirm(trimmedComment)
  }

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth data-testid={`${testIdPrefix}.dialog`}>
      <Box className='flex justify-end px-4 pt-4'>
        <IconButton size='small' onClick={onClose} data-testid={`${testIdPrefix}.confirmation-close-icon-button`}>
          <CloseOutlined className='text-blue-gray' />
        </IconButton>
      </Box>
      <DialogContent classes={{ root: "px-10 pt-0 pb-6" }}>
        {banner && <Box className='mb-4'>{banner}</Box>}
        <Box className='mb-6 w-full justify-center text-center'>
          <WarningOutlined className='size-[48px] text-secondary-600' />
          <Typography
            color='primary'
            className='mt-6 text-xl font-normal leading-[133%] tracking-[-1px]'
            component='h2'
            data-testid={`${testIdPrefix}.confirmation-title`}
          >
            {title}
          </Typography>
        </Box>
        {content && (
          <Typography
            className='mb-6 px-3 text-center text-base leading-[150%] tracking-[-0.85px] text-gray-700'
            component='div'
            data-testid={`${testIdPrefix}.confirmation-content`}
          >
            {content}
          </Typography>
        )}
        <AerosTextField
          value={comment}
          onChange={setComment}
          label={commentLabel}
          placeholder={commentPlaceholder}
          multiline
          minRows={3}
          required
          disabled={isSubmitDisabled}
          slotProps={{ htmlInput: { maxLength: 500 } }}
          data-testid={`${testIdPrefix}.comment-field`}
        />
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={onClose} data-testid={`${testIdPrefix}.confirmation-close-button`}>
          {closeLabel}
        </Button>
        <Button
          variant={warning ? "error" : "primary"}
          onClick={handleConfirm}
          disabled={!isConfirmEnabled}
          data-testid={`${testIdPrefix}.confirmation-submit-button`}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
